import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Profile } from 'consts/consts';
import { DataContext, LoadingContext } from 'contexts';
import { useLocalized } from 'utils/useLocalized';
import CoefficientInput from './CoefficientInput';
import i18nStrings from './strings';
import styles from './styles.modules.scss';

const Coefficients = ({searchCoef, setSearchCoef, saveRatingParams}) => {
    const strings = useLocalized(i18nStrings);
    const { ratingParams } = useContext(DataContext);
    const { ratingParamsIsLoading } = useContext(LoadingContext);
    const [rt, setRt] = useState(searchCoef?.realTrucker);
    const [act, setAct] = useState(searchCoef?.actualTrucks);
    const [ant, setAnt] = useState(searchCoef?.analyticsTrucks);
    const [profile, setProfile] = useState(searchCoef?.profile);

    useEffect(() => {
        if (rt !== searchCoef?.realTrucker) setRt(searchCoef?.realTrucker);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCoef]);

    useEffect(() => {
        if (act !== searchCoef?.actualTrucks) setAct(searchCoef?.actualTrucks);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCoef]);

    useEffect(() => {
        if (ant !== searchCoef?.analyticsTrucks) setAnt(searchCoef?.analyticsTrucks);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCoef]);

    useEffect(() => {
        if (JSON.stringify(profile) !== JSON.stringify(searchCoef?.profile)) setProfile(searchCoef?.profile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCoef]);

    const onChangeCoef = useCallback((name, value) => {
        setSearchCoef(prevState => ({
            ...prevState,
            [name]: value
        }));
    },[setSearchCoef]);

    const normalizeEvt = useCallback((event) => event.target.value?.replace(',', '.') || null,[]);
    const handleRtChange = useCallback((event) => setRt(normalizeEvt(event)), [normalizeEvt]);
    const handleActChange = useCallback((event) => setAct(normalizeEvt(event)), [normalizeEvt]);
    const handleAntChange = useCallback((event) => setAnt(normalizeEvt(event)), [normalizeEvt]);
    const handleProfileChange = useCallback((event, profileId) => setProfile(prevState => {
        const val = normalizeEvt(event);
        if (val) {
            return {...prevState, [profileId]: val};
        }

        const copy = {...prevState};
        delete copy[profileId];
        return copy;
    }), [normalizeEvt]);

    const handleOnBlurRt = useCallback(() => onChangeCoef('realTrucker', rt), [rt, onChangeCoef]);
    const handleOnBlurAct = useCallback(() => onChangeCoef('actualTrucks', act), [act, onChangeCoef]);
    const handleOnBlurAnt = useCallback(() => onChangeCoef('analyticsTrucks', ant), [ant, onChangeCoef]);
    const handleOnBlurProfile = useCallback(() => onChangeCoef('profile', profile), [profile, onChangeCoef]);

    const handleClear = useCallback(() => {
        setSearchCoef(() => null);
    }, [setSearchCoef]);

    const handleFillSaved = useCallback(() => {
        setSearchCoef(() => ({
            realTrucker: ratingParams?.realTrucker,
            actualTrucks: ratingParams?.actualTrucks,
            analyticsTrucks: ratingParams?.analyticsTrucks,
            profile: ratingParams?.profile,
        }));
    }, [ratingParams, setSearchCoef]);

    const profilePlaceholders = useMemo(() => ({
        [Profile.trucker]: '1',
        [Profile.owner_trucker]: '1',
        [Profile.exp_trucker]: '0.9',
        [Profile.exp]: '0.65',
    }), []);

    const profiles = useMemo(() => [
        Profile.trucker,
        Profile.owner_trucker,
        Profile.exp_trucker,
        Profile.exp,
        Profile.dispatcher,
        Profile.owner,
        Profile.insurance,
        Profile.jur,
        Profile.autoService,
        Profile.it,
        Profile.default,
    ], []);

    return (
        <div className={styles['coefficients']}>
            <div className={styles['base']}>
                <CoefficientInput
                    placeholder="0.6"
                    ratingParamsIsLoading={ratingParamsIsLoading}
                    savedValue={ratingParams?.realTrucker}
                    title={strings.realTrucker}
                    value={rt}
                    onBlur={handleOnBlurRt}
                    onChange={handleRtChange}
                />
                <CoefficientInput
                    placeholder="0.6"
                    ratingParamsIsLoading={ratingParamsIsLoading}
                    savedValue={ratingParams?.actualTrucks}
                    title={strings.actualTrucks}
                    value={act}
                    onBlur={handleOnBlurAct}
                    onChange={handleActChange}
                />
                <CoefficientInput
                    placeholder="2"
                    ratingParamsIsLoading={ratingParamsIsLoading}
                    savedValue={ratingParams?.analyticsTrucks}
                    title={strings.analyticsTrucks}
                    value={ant}
                    onBlur={handleOnBlurAnt}
                    onChange={handleAntChange}
                />
                <button
                    className="glz-button glz-is-small glz-is-error glz-is-outlined"
                    onClick={handleClear}
                >
                    {strings.clear}
                </button>
                <button
                    className="glz-button glz-is-small"
                    onClick={handleFillSaved}
                >
                    {strings.fillSaved}
                </button>
                <button
                    className="glz-button glz-is-small glz-is-primary"
                    onClick={saveRatingParams}
                >
                    {strings.save}
                </button>
            </div>
            <div className={styles['profile']}>
                {profiles.map(profileType => (
                    <CoefficientInput
                        key={profileType}
                        placeholder={profilePlaceholders[profileType] || '0.45'}
                        ratingParamsIsLoading={ratingParamsIsLoading}
                        savedValue={(ratingParams?.profile && ratingParams.profile[profileType]) || null}
                        title={strings.profile[profileType]}
                        value={(profile && profile[profileType]) || null}
                        onBlur={handleOnBlurProfile}
                        onChange={(e) => handleProfileChange(e, profileType)}
                    />
                ))}
            </div>
        </div>
    );
};

export default Coefficients;

Coefficients.propTypes = {
    saveRatingParams: PropTypes.func,
    searchCoef: PropTypes.number,
    setSearchCoef: PropTypes.func,
};
